import React, { useEffect, useState } from 'react';
import { Mail, X } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI, setAuthToken } from '../utils/api';

/**
 * Dashboard banner shown to users who skipped email verification at signup.
 * Lets them enter the 6-digit code or request a new one — without leaving
 * the dashboard.
 *
 * Props:
 *   user      — { email, email_verified, is_admin, name }
 *   onVerified — callback invoked after a successful verify
 */
const EmailVerificationBanner = ({ user, onVerified }) => {
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const id = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    return () => clearTimeout(id);
  }, [resendCooldown]);

  // Hide for admins or verified users or if user dismissed for this session
  if (!user || user.email_verified || user.is_admin) return null;
  if (dismissed) return null;

  const handleResend = async () => {
    if (resendCooldown > 0) return;
    try {
      await authAPI.resendVerification(user.email);
      toast.success(`Yeni kod ${user.email} adresine gönderildi`);
      setResendCooldown(60);
      setExpanded(true);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Kod gönderilemedi';
      toast.error(detail);
      setResendCooldown(30);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) {
      toast.error('Lütfen 6 haneli kodu girin');
      return;
    }
    setLoading(true);
    try {
      const res = await authAPI.verifyEmail(user.email, code);
      if (res?.data?.access_token) {
        setAuthToken(res.data.access_token);
      }
      toast.success('E-postanız doğrulandı! 🎉');
      setCode('');
      setExpanded(false);
      if (onVerified) onVerified();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Kod doğrulanamadı');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="mb-6 p-4 rounded-xl border border-amber-300 bg-gradient-to-r from-amber-50 to-yellow-50 shadow-sm"
      data-testid="email-verification-banner"
    >
      <div className="flex items-start gap-3">
        <Mail className="w-5 h-5 text-amber-700 mt-1 shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="font-bold text-amber-900">
                E-posta adresinizi doğrulayın
              </p>
              <p className="text-sm text-amber-800 mt-0.5">
                <span className="font-medium">{user.email}</span> adresine bir kod gönderildi.
                Spam klasörünü de kontrol edin.
              </p>
            </div>
            <button
              onClick={() => setDismissed(true)}
              className="text-amber-700 hover:text-amber-900 p-1 rounded-md hover:bg-amber-100"
              aria-label="Kapat"
              data-testid="verification-banner-dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {!expanded ? (
            <div className="mt-3 flex gap-2 flex-wrap">
              <button
                onClick={() => setExpanded(true)}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-md"
                data-testid="verification-banner-enter-code"
              >
                Kodu Gir
              </button>
              <button
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="px-4 py-2 bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="verification-banner-resend"
              >
                {resendCooldown > 0
                  ? `Tekrar gönder (${resendCooldown}s)`
                  : 'Yeni Kod Gönder'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleVerify} className="mt-3 flex gap-2 flex-wrap items-center" data-testid="verification-banner-form">
              <input
                type="text"
                inputMode="numeric"
                pattern="\d{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                autoFocus
                className="px-3 py-2 w-32 border border-amber-300 rounded-md text-center text-lg tracking-[4px] font-mono focus:outline-none focus:ring-2 focus:ring-amber-400"
                data-testid="verification-banner-code-input"
              />
              <button
                type="submit"
                disabled={loading || code.length !== 6}
                className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium rounded-md disabled:opacity-50"
                data-testid="verification-banner-submit"
              >
                {loading ? '...' : 'Doğrula'}
              </button>
              <button
                type="button"
                onClick={handleResend}
                disabled={resendCooldown > 0}
                className="text-amber-700 hover:text-amber-900 text-sm underline disabled:text-gray-400 disabled:no-underline disabled:cursor-not-allowed"
                data-testid="verification-banner-resend-2"
              >
                {resendCooldown > 0 ? `Yeniden gönder (${resendCooldown}s)` : 'Kodu yeniden gönder'}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationBanner;
